import React, {useEffect, useRef} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {saveTokenCookies} from '@lightricks/fortress-web-client';
import parseJwt from '@lightricks/fortress-web-client/dist/helpers/jwt-parser';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import Fortress from '@/services/fortress/Fortress';
import {flowNames, reasons} from '@/lib/delta/deltaConstants.js';
import queryClient from '@/utils/reactQueryClient';
import {Token} from '@/utils/token';
import {ROUTES, UNAUTHENTICATED_ROUTES} from '@/config/routesConstants';
import authRequests from '@/api/auth';
import useLtxToken from '@/hooks/use-ltx-token';
import useNavigation from '@/hooks/use-navigation';
import useToken from '@/hooks/use-token';
import {redirectAfterAuthActions} from '@/stores/redirectAfterAuthStore';

export interface DecodedToken {
  token: Token;

  token_cookies: {
    token: string;
    refreshToken: string;
    refreshTokenExpirationS: number;
    isNew: boolean;
  };
}

function useRevalidateSession({
  signupRoute = ROUTES.SIGN_UP,
  loginRoute = ROUTES.LOGIN,
}) {
  const {isAuthenticated, initialized, setToken, resetToken} = useToken(true);
  const {setLtxToken} = useLtxToken();
  const [isRevalidatingSession, setIsRevalidatingSession] =
    React.useState(false);
  const location = useLocation();
  const navigation = useNavigation();
  const isRevalidatingSessionRef = useRef(false);
  const hasProcessedTokenRef = useRef(false);
  const flowEvent = AnalyticsService.getFlow(flowNames.AUTH.NEW_SIGNUP) || {};

  const [searchParams, setSearchParams] = useSearchParams();
  const tokenParam = searchParams.get('token');

  const clearToken = async () => {
    Fortress.clearCookieTokens();
    await resetToken();
    queryClient.clear();
  };

  const setRedirectUrlAfterAuth = () => {
    redirectAfterAuthActions.setRedirectUrlAfterAuth(
      `${location.pathname}${location.search}`
    );
  };

  useEffect(() => {
    (async () => {
      if (
        initialized &&
        !isRevalidatingSession &&
        !isRevalidatingSessionRef.current
      ) {
        setIsRevalidatingSession(true);
        isRevalidatingSessionRef.current = true;

        if (tokenParam && !hasProcessedTokenRef.current) {
          // Handle token from URL parameters first (runs once on mount)
          hasProcessedTokenRef.current = true;
          try {
            const decodedToken = parseJwt(tokenParam) as DecodedToken;

            if (decodedToken.token_cookies) {
              saveTokenCookies(decodedToken.token_cookies);
              setLtxToken(decodedToken.token_cookies.token);
            }

            await setToken(decodedToken.token);

            sendAuthStepEndedEvent({
              searchParams,
              flowId: flowEvent.flow_id,
            });

            // Clear the token from URL after processing
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('token');
            newSearchParams.delete('type');
            newSearchParams.delete('provider');
            setSearchParams(newSearchParams, {replace: true});
          } catch (error) {
            console.error(
              'Failed to decode token from query parameters:',
              error
            );
          } finally {
            isRevalidatingSessionRef.current = false;
            setIsRevalidatingSession(false);
          }
          return; // Exit early after processing token
        }

        try {
          const fortressToken = await Fortress.getAccessToken();
          if (fortressToken && !isAuthenticated) {
            const {email} = parseJwt(fortressToken);
            const authToken = await authRequests.login({
              email,
              password: fortressToken,
            });
            await setToken(authToken);
          } else if (!fortressToken && isAuthenticated) {
            await clearToken();
            setRedirectUrlAfterAuth();
            navigation.navigate(loginRoute, undefined, false);
          } else {
            const isUnauthenticatedRoute = !!UNAUTHENTICATED_ROUTES.find(
              (route) => new RegExp(route, 'i').test(location.pathname)
            );
            if (!fortressToken && !isAuthenticated && !isUnauthenticatedRoute) {
              await clearToken();
              setRedirectUrlAfterAuth();
              navigation.navigate(loginRoute, undefined, false);
            }
          }
        } catch (e: any) {
          const urlParams = new URLSearchParams(window.location.search);
          const isSignUpReactWithEmail =
            location.pathname.includes(signupRoute) && urlParams.has('email');

          // This is a special case where a user is prepaid using web payment flow and he's coming to the sign up page
          // with the auth cookies although its user is not exists on our end so he needs to sign up in our end as well.
          // in that case, we don't want to redirect him to the login but keep him in the sign up page but clear the cookies.
          if (!isSignUpReactWithEmail) {
            await clearToken();
            setRedirectUrlAfterAuth();
            navigation.navigate(loginRoute, undefined, false);
          } else if (e.response?.status === 401) {
            clearToken();
            setRedirectUrlAfterAuth();
            // We reload the page because otherwise we are being redirected back to login after signing up
            window.location.reload();
          }
        }
        isRevalidatingSessionRef.current = false;
        setIsRevalidatingSession(false);
      }
    })();
  }, [initialized, isAuthenticated, location.pathname]);

  return isRevalidatingSession;
}

type SendAuthStepEndedEventParams = {
  searchParams: URLSearchParams;
  flowId: string;
};

function sendAuthStepEndedEvent({
  searchParams,
  flowId,
}: SendAuthStepEndedEventParams) {
  const provider = searchParams.get('provider');

  let eventName;
  let eventProperties = {};

  if (searchParams.get('type') === 'signup') {
    eventName = eventNames.SIGNUP_STEP_ENDED;
    eventProperties = {signup_provider: provider};
  } else if (searchParams.get('type') === 'login') {
    eventName = eventNames.LOGIN_STEP_ENDED;
    eventProperties = {login_provider: provider};
  }

  if (eventName !== undefined) {
    AnalyticsService.dispatchEvent(eventName, {
      ...eventProperties,
      flow_id: flowId || '',
      reason: reasons.AUTH.SUCCESS,
      error: '',
      process_id: '',
      request_type: '',
      input_value: '',
      number_of_required_fields: 0,
      number_of_fields_completed: 0,
    });
  }
}

export default useRevalidateSession;
